import Box from '@mui/material/Box';
import { useTheme } from "@mui/material/styles";
import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { viewItems } from "../../components/constants";
import { filterContext } from "../../stores/filters/filterContext";
import { messageContext } from "../../stores/messages/messageContext";
import { userContext } from "../../stores/users/userContext";
import {
    CURRENT_VIEW_CONFIG,
    DRAG_TOOLBAR_HEIGHT,
    makeVideoUrl,
    mapViewToModel,
    maxLines,
    optionToKind,
} from '../constants';
import getCookies from "../CSRF/getCookies";
import Header from "../header/header";
import {
    AIBHeader,
    autoHighlightHeader,
    categoriesHeader,
    contentModerationHeader,
    entitiesHeader,
    sentencesHeader
} from "../header/headerConstants";
import { ItemContent } from '../Items/ItemContent';
import Loading from "../loading/loading";
import ChartsGrid from '../views/ChartsGrid';
import CherryPickGrid from '../views/CherryPickGrid';
import { getContentStyle } from "../views/getContentStyle";
import DraggableToolbar from "./draggableToolbar";
import { reGex } from "./regex";
import {
    getSentencesStreamUrl,
    CREATE_VIDEO,
    CHART,
    CHERRY_PICK,
} from '../constants';

const INTERVAL = 75;




const CreateVideo = () => {
    const userStore = useContext(userContext)
    const filterStore = useContext(filterContext)
    const messageStore = useContext(messageContext);
    const transcriptId = userStore.getUserTranscriptId()

    const [loading, setLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);

    const [originalRows, setOriginalRows] = useState([]);
    const [originalEntities, setOriginalEntities] = useState([]);
    const [originalCategories, setOriginalCategories] = useState([]);
    const [originalAIB, setOriginalAIB] = useState([]);
    const [originalAutoHighlight, setOriginalAutoHighlight] = useState([]);
    const [originalContentModeration, setOriginalContentModeration] = useState([]);
    const [rows, setRows] = useState([]);

    const [header, setHeader] = useState(sentencesHeader)
    const [headerEntities, setHeaderEntities] = useState(entitiesHeader)
    const [headerAutoHighlight, setHeaderAutoHighlight] = useState(autoHighlightHeader)
    const [headerAIB, setHeaderAIB] = useState(AIBHeader)
    const [headerCategories, setHeaderCategories] = useState(categoriesHeader)
    const [headerContentModeration, setHeaderContentModeration] = useState(contentModerationHeader)

    const [precision, setPrecision] = useState(viewItems.reduce((acc, item) => {
        acc[item] = false;
        return acc;
    }, {}));

    const [order, setOrder] = useState(viewItems.reduce((acc, item) => {
        acc[item] = 1;
        return acc;
    }, {}));
    const [checked, setChecked] = useState(viewItems.reduce((acc, item) => {
        acc[item] = [false, false]; // [checked, indeterminate] for each view
        return acc;
    }, {}));
    const [selectedCount, setSelectedCount] = useState(viewItems.reduce((acc, item) => {
        acc[item] = 0
        return acc
    }, {}));

    const [videoFile, setVideoFile] = useState('')
    const [videoExt, setVideoExt] = useState('')
    const [currentView, setCurrentView] = useState('Create a Video');
    const [viewConfig, setViewConfig] = useState(CURRENT_VIEW_CONFIG[currentView]);
    const [closeSearchModalRef, setCloseSearchModalRef] = useState(null);

    const sentClasses = { '0': 'positive', '1': 'negative', '2': 'neutral' }
    const [toolbarPosition, setToolbarPosition] = useState('right');
    const [firstVisibleIndex, setFirstVisibleIndex] = useState(0);
    const [scrollTimeDisplay, setScrollTimeDisplay] = useState({ show: false, time: '', position: { x: 0, y: 0 } });
    const scrollTimeoutRef = useRef(null);
    const scrollerRef = useRef(null); // Ref for the scroll container
    const virtuosoRef = useRef(null); // Ref for TableVirtuoso navigation

    const theme = useTheme();
    const mode = theme.palette.mode;
    const subtitleColorHighlight = mode === 'dark' ? 'orange' : 'red'

    const getOriginalDataForCurrentView = (view = currentView) => {
        if (filterStore.getSpaces(view)) {
            return originalRows.map(obj => ({ ...obj, order: maxLines }))
        }
        return originalRows.filter(obj => !obj['space']).map(obj => ({ ...obj, order: maxLines }))
    }

    const getDataForCurrentView = (view = currentView) => {
        // get a fresh copy of the data for the current view
        // and add the order field to the data
        // filter out the spaces if the spaces are off
        switch (view) {
            case 'AIB':
                return originalAIB.map(obj => ({ ...obj, order: maxLines }));
            case 'Auto Highlight':
                return originalAutoHighlight.map(obj => ({ ...obj, order: maxLines }));
            case 'Entities':
                return originalEntities.map(obj => ({ ...obj, order: maxLines }));
            case 'Categories':
                return originalCategories.map(obj => ({ ...obj, order: maxLines }));
            case 'Content Moderation':
                return originalContentModeration.map(obj => ({ ...obj, order: maxLines }));
            case 'Charts':
            case 'Cherry Pick':
            case 'Create a Video':
                return getOriginalDataForCurrentView(view)
            default:
                console.log('default should not happen')
                return getOriginalDataForCurrentView(view)
        }
    }

    const getHeaderCurrentView = () => {
        switch (currentView) {
            case 'AIB':
                return headerAIB;
            case 'Auto Highlight':
                return headerAutoHighlight;
            case 'Categories':
                return headerCategories;
            case 'Content Moderation':
                return headerContentModeration;
            case 'Entities':
                return headerEntities;
            case 'Create a Video':
            default:
                return header;
        }
    }

    const updateHeaderDataView = (newHeader) => {
        switch (currentView) {
            case 'AIB':
                return setHeaderAIB(newHeader);
            case 'Auto Highlight':
                return setHeaderAutoHighlight(newHeader);
            case 'Categories':
                return setHeaderCategories(newHeader);
            case 'Content Moderation':
                return setHeaderContentModeration(newHeader);
            case 'Entities':
                return setHeaderEntities(newHeader);
            case 'Create a Video':
            default:
                return setHeader(newHeader);
        }
    }

    const AdjaceSpaces = (rows) => {
        let stack = []
        for (let i = 0; i < rows.length; i++) {

            if (rows[i]['space']) {
                stack.push(i)  // Store array index instead of ID
            } else {
                rows[i]['related'] = stack;
                stack = []
            }
        }
        return rows
    }

    useEffect(() => {
        if (transcriptId === null) return;

        const fetchDataStream = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${getSentencesStreamUrl}${transcriptId}/${userStore.getToken()}/`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                setLoadingProgress(0)
                const reader = response.body.getReader();
                let buffer = '';
                let metadata = null;
                let sentences = [];
                let entity = [];
                let category = [];
                let aib = [];
                let auto_highlight = [];
                let content_moderation = [];
                let rowsProcessed = 0; // Track processed rows for gzip-safe progress
                let lastReportedProgress = 0; // Track last reported progress to throttle updates
                let video_duration = 0;
                let video_file = '';
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    // Convert the chunk to text and add to buffer
                    buffer += new TextDecoder().decode(value);

                    // Process complete lines
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || ''; // Keep the incomplete line in buffer

                    for (const line of lines) {
                        if (!line) continue;
                        const data = JSON.parse(line);

                        // Handle metadata (first line)
                        if (!metadata && data.total_rows) {
                            metadata = data;
                            video_duration = metadata.video_duration;
                            video_file = metadata.video;
                            setVideoExt(metadata.ext);
                            continue;
                        }

                        // Update progress using row count (gzip-safe)
                        if (!metadata?.total_rows) continue;

                        rowsProcessed++;
                        const progress = Math.round((rowsProcessed / metadata.total_rows) * 100);

                        // Only update React state occasionally for the Loading component
                        if (progress - lastReportedProgress >= 10 || progress === 100) {
                            setLoadingProgress(progress);
                            lastReportedProgress = progress;
                        }

                        // Process data based on type
                        switch (data.type) {
                            case 'sentence':
                                if (!data.data) continue;
                                sentences.push(data.data);
                                break;
                            case 'entity':
                                entity.push(data.data);
                                break;
                            case 'category':
                                category.push(data.data);
                                break;
                            case 'aib':
                                aib.push(data.data);
                                break;
                            case 'auto_highlight':
                                auto_highlight.push(data.data);
                                break;
                            case 'content_moderation':
                                content_moderation.push(data.data);
                                break;
                            default:
                        }
                    }
                }

                if (sentences.length > 0) {
                    sentences = sentences.map((obj) => {
                        obj['order'] = maxLines;
                        obj['sentiment'] = sentClasses[obj['sentiment']];
                        obj['sec_int'] = parseInt(obj['sec']);
                        obj['sec_float'] = parseFloat(obj['sec']);
                        obj['related'] = null
                        return obj;
                    });

                    sentences = AdjaceSpaces(sentences)
                    setOriginalRows(sentences);
                    sentences = sentences.filter(obj => !obj['space'])
                    setRows(sentences);
                    // calcDuration(sentences)
                    resetFilters(sentences);
                    indeterminateCheck(sentences);
                    const speakers = getDimension('speaker', sentences)
                    filterStore.setSpeakers(speakers, CREATE_VIDEO)
                    filterStore.setSpeakers(speakers, CHART)
                    filterStore.setSpeakers(speakers, CHERRY_PICK)

                }

                setOriginalEntities(makeOrder(entity))
                setOriginalCategories(makeOrder(category))
                setOriginalAIB(makeOrder(aib))
                setOriginalAutoHighlight(makeOrder(auto_highlight))
                setOriginalContentModeration(makeOrder(content_moderation))

                setLoadingProgress(100);
                setLoading(false);
                setVideoFile(video_file);
                userStore.setTotalDuration(durationFormat(video_duration * 1000));

            } catch (error) {
                console.error("Error fetching transcript data:", error);
                messageStore.setMessage('Failed to load transcript data.', 'error');
                setLoading(false);
            }

        }

        // Call the fetchDataStream function
        fetchDataStream().then(() => {
            filterAll()
        }).catch((error) => {
            console.error("Error in fetchDataStream:", error);
        });

    }, [transcriptId])

    // Cleanup scroll timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);


    const handleToolbarPositionChange = (newPosition) => setToolbarPosition(newPosition)

    const durationFormat = (duration) => new Date(duration).toISOString().slice(11, (precision[currentView] ? -1 : -5));

    const calcDuration = (data) => {
        debugger
        console.log('calcDuration', data)
        // Use the order field to identify selected items (items with order < maxLines are selected)
        //calculate the duration of the selected items this is the order != maxLines
        // Add safety check to filter out undefined/null objects
        const validData = data.filter(obj => obj && obj.hasOwnProperty('order') && obj.hasOwnProperty('duration_ms'));
        const durationSelected = validData.filter(obj => obj['order'] !== maxLines);

        const durationSelectedMs = durationSelected.reduce((a, b) => a + b['duration_ms'], 0);
        const durationAllMs = validData.reduce((a, b) => a + b['duration_ms'], 0);
        userStore.setDurationInMs(durationSelectedMs);
        userStore.setDuration(durationFormat(durationSelectedMs));
        userStore.setTotalDuration(durationFormat(durationAllMs));
    }

    const filterAll = () => {
        shouldStopVideo()
        let filterRows = [...getDataForCurrentView(currentView)];
        if (filterRows.length === 0)
            return

        filterRows = filterSearch(filterRows)
        filterRows = filterSentiment(filterRows)
        filterRows = filterSpeakers(filterRows)
        filterRows = filterSeconds(filterRows)
        filterRows = filterTextLen(filterRows)
        setRows(filterRows)
        calcDuration(filterRows)
    }

    const filterSentiment = (filterRows) => {
        if (!filterRows.length || !('sentiment' in filterRows[0])) return filterRows;

        const sentimentKeys = new Set(filterStore.getSentimentsTrue(currentView));
        if (!sentimentKeys.size) return filterRows;

        return filterRows.filter(row => sentimentKeys.has(row.sentiment))
    };

    const filterSpeakers = (filterRows) => {
        if (!filterRows.length || !('speaker' in filterRows[0])) return filterRows;

        const speakerKeys = new Set(filterStore.getSpeakersTrue(currentView));
        if (!speakerKeys.size) return filterRows;

        return filterRows.filter(row => speakerKeys.has(row.speaker));
    };

    const filterSeconds = (filterRows) => {
        if (!filterRows.length || !('sec' in filterRows[0])) return filterRows;

        const [min, max] = filterStore.getSeconds(currentView);
        if (!precision[currentView]) {
            return filterRows.filter(row => row.sec_int >= min && row.sec_int <= max);
        }
        return filterRows.filter(row => row.sec_float >= min && row.sec_float <= max);
    };

    const filterTextLen = (filterRows) => {
        if (!filterRows.length || !('length' in filterRows[0])) return filterRows;
        let [min, max] = filterStore.getTextLen(currentView);
        if (filterStore.getSpaces(currentView)) {
            min = 0
        }
        return filterRows.filter(row => row['length'] >= min && row['length'] <= max);
    };

    const filterSearch = (filterRows) => {
        const searchString = filterStore.getSearchEscaped(currentView);

        if (!searchString) return filterRows;

        const [regex, regex1, regex2] = reGex(searchString);
        if (filterStore.getSpaces(currentView)) {
            let newRows = []
            for (let i = 0; i < filterRows.length; i++) {
                if (filterRows[i]['sentence'] && (regex.test(filterRows[i]['sentence']) || regex1.test(filterRows[i]['sentence']) || regex2.test(filterRows[i]['sentence']))) {
                    // Add related spaces using the original rows array and stored indices
                    if (filterRows[i]['related'] && filterRows[i]['related'].length > 0) {
                        const relatedIndices = [...filterRows[i]['related']]; // Copy array to avoid mutation
                        for (const index of relatedIndices) {
                            if (originalRows[index] && originalRows[index]['space']) {
                                newRows.push(originalRows[index]);
                            }
                        }
                    }
                    newRows.push(filterRows[i])
                }
            }
            return newRows
        }
        // Spaces are hidden - include only matching content
        return filterRows.filter((obj) => {
            return obj['sentence'] && (regex.test(obj['sentence']) || regex1.test(obj['sentence']) || regex2.test(obj['sentence']));
        });
    }

    const onSentimentChange = (event) => {
        shouldStopVideo()
        filterStore.setSentiments(event.target.value, currentView)
        filterAll()
    }

    const onSpeakerChange = (event) => {
        shouldStopVideo()
        filterStore.setResetSpeakers(event.target.value, currentView)
        filterAll()
    }

    const onSecChange = (values) => {
        shouldStopVideo()
        filterStore.setSeconds(values, currentView)
        filterAll()
    }

    const onSetSpaces = () => {
        shouldStopVideo()

        // Get the currently visible item before toggling spaces
        let currentlyVisibleItem = null;
        if (rows.length > 0 && firstVisibleIndex < rows.length) {
            currentlyVisibleItem = rows[firstVisibleIndex];
            console.log('Preserving position for item:', {
                id: currentlyVisibleItem?.id,
                sentence: currentlyVisibleItem?.sentence?.substring(0, 30) + '...',
                currentIndex: firstVisibleIndex,
                spacesCurrentlyOn: filterStore.getSpaces(currentView)
            });
        }

        // Toggle spaces and apply all filters
        filterStore.setSpacesOnOff(currentView)

        // Apply all filters - this will update rows state and call calcDuration
        filterAll();

        // Find the item in the new data and restore position
        if (currentlyVisibleItem && virtuosoRef.current) {
            // Use a timeout to allow filterAll() to complete and update the rows state
            setTimeout(() => {
                if (rows.length > 0) {
                    // Find the item in the new filtered data
                    const newIndex = rows.findIndex(row => row.id === currentlyVisibleItem.id);

                    console.log('Restoring position:', {
                        itemId: currentlyVisibleItem.id,
                        oldIndex: firstVisibleIndex,
                        newIndex: newIndex,
                        newRowsLength: rows.length,
                        spacesNowOn: filterStore.getSpaces(currentView)
                    });

                    if (newIndex !== -1) {
                        virtuosoRef.current.scrollToIndex({
                            index: newIndex,
                            align: 'start',
                            behavior: 'auto'
                        });
                    }
                }
            }, 100);
        }
    }

    const onTextLenChange = (values) => {
        shouldStopVideo()
        filterStore.setTextLen(values, currentView)
        filterAll()
    }

    const onSearchChange = (event) => {
        shouldStopVideo()
        const searchTerm = event.target.value.toLowerCase();
        filterStore.setSearch(currentView, searchTerm)
        filterAll()
    }

    const resetFilters = (data, view = currentView) => {
        shouldStopVideo()
        // Only reset filters for specified view
        filterStore.resetFilters(
            getDimensionMinMax('length', data),
            getDimensionMinMax('sec', data),
            view
        )
        filterStore.resetAllSpeakers()
    }

    const updateHeader = () => {
        const newHeader = getHeaderCurrentView().map((item) => {
            item.isUp = false
            return item
        })
        updateHeaderDataView(newHeader)
    }

    const onResetFilters = () => {
        shouldStopVideo()
        let newRows = [...getDataForCurrentView(currentView)]
        resetFilters(newRows)
        // Only reset order for current view
        setOrder(prevOrder => ({
            ...prevOrder,
            [currentView]: 1
        }));

        setSelectedCount(prevCount => ({
            ...prevCount,
            [currentView]: 0
        }));

        updateHeader()
        indeterminateCheck(newRows)
        // calcDuration(newRows)
        setRows(newRows)
        filterAll()

        // Scroll to top after reset
        // only if the first visible index is not 0
        if (firstVisibleIndex !== 0) {
            setTimeout(() => {
                virtuosoRef.current?.scrollToIndex({ index: 0 });
            }, 100);
        }
    }

    const getPrecisionField = (field) => {
        if (field === 'sec') {
            return precision[currentView] ? 'sec_float' : 'sec_int'
        }
        return field
    }

    const getDimensionMinMax = (field, obj) => {
        field = getPrecisionField(field)
        // take in account the spaces on off  
        const dimension = [...new Set(obj.map(obj => obj[field]))]
        return [Math.min(...dimension), Math.max(...dimension)];
    }

    const getDimension = (field, obj) =>
        [...new Set(obj.map(o => o[field]))]
            .filter(Boolean)
            .sort();

    const setColor = (id, colorClass, op) => {
        const element = document.getElementById(id)
        if (element) {
            if (op === '-') {
                element.classList.remove(colorClass)
                return
            }
            element.classList.add(colorClass)
        }
    }

    const countRows = () => getDataForCurrentView().length;

    const showSubtitles = () => {
        // Actually, the elapsed time is not exactly INTERVAL_MS maybe a bit more or less
        // special in the Firefox browser

        const elapsed = (performance.now() - userStore.getStartTime()) / 1000
        userStore.setStartTime(performance.now())

        if (!filterStore.getSubtitlesOnOff(currentView)) {
            // keep updating the timeline, just in case a user changes their mind
            userStore.setSentenceStart(userStore.getSentenceStart() + elapsed)
            return
        }
        const timeLine = userStore.getSentenceStart()
        const previousWord = userStore.getPreviousWord()
        const item = userStore.getItem()
        const data = userStore.getVideoData()
        const wordIndex = data['utterance'].findIndex((item) => timeLine >= item[0] && timeLine <= item[1])

        userStore.setSentenceStart(timeLine + elapsed)
        setColor(`s-${item}-${previousWord}`, subtitleColorHighlight, '-')
        setColor(`s-${item}-${wordIndex}`, subtitleColorHighlight, '+')
        userStore.setPreviousWord(wordIndex)
    }

    const onSetResetSubtitles = () => {
        // Store the current subtitles state before toggling
        const currentSubtitlesEnabled = filterStore.getSubtitlesOnOff(currentView);
        // Toggle the subtitles state
        filterStore.setSubtitlesOnOff(currentView)
        // If subtitles are being turned OFF and there's video data, clear any highlighted subtitles
        if (currentSubtitlesEnabled && userStore.getVideoData() !== null) {
            clearSubtitles()
        }
    }

    const clearSubtitles = () => {
        // It ends before the word has a chance to be delighted,
        // so this clears the highlight word when the video stops
        // no matter when it stops
        const item = userStore.getItem()
        const data = userStore.getVideoData()

        for (let i = 0; i < data['utterance'].length; i++) {
            setColor(`s-${item}-${i}`, subtitleColorHighlight, '-')
        }
    }

    const shouldStopVideo = () => (userStore.getItem() !== null ? videoStop() : null)

    const handleRangeChanged = (range) => {
        // Stop video if is not visible
        const playing = userStore.getItem()
        if (playing === null) return
        if (!(playing > range.startIndex && playing < range.endIndex)) {
            videoStop()
        }
    }

    const playFired = () => {
        const durationMs = userStore.getVideoDurationMs()
        userStore.setTimeOutId(setTimeout(videoStop, durationMs))
        userStore.setStartTime(performance.now())
        userStore.setIntervalId(setInterval(() => showSubtitles(), INTERVAL))
    }

    const videoStop = () => {
        const video = userStore.getVideo()

        if (video) {
            clearInterval(userStore.getIntervalId())
            clearTimeout(userStore.getTimeOutId())
            video.pause();

            // Safari-compatible poster restoration
            const thumbnail = userStore.getVideoData().thumbnail;
            if (thumbnail) {
                // First remove the poster
                video.removeAttribute('poster');
                // Force a reflow
                void video.offsetWidth;
                // Then set it again
                video.setAttribute('poster', thumbnail);
            }

            clearSubtitles()
            video.removeEventListener('play', playFired)
            userStore.resetVideo()
        }
    }

    const startVideo = () => {
        const video = userStore.getVideo()

        if (video.paused || video.ended) {
            video.addEventListener("play", playFired)
            video.currentTime = userStore.getStartIn()
            video.play();
        } else {
            videoStop()
        }
    }

    const onVideoClick = (event, data, item) => {
        const previousVideo = userStore.getVideo()
        if (previousVideo !== event.target) {
            videoStop()
        }
        const video = event.target;
        if (video.paused || video.ended) {
            userStore.setVideo(data, video, item)
            startVideo()
        } else {
            videoStop()
        }
    };

    const makeOrder = (buffer) => {
        // add order to the buffer
        buffer = buffer.map((obj) => {
            obj['order'] = maxLines;
            obj['space'] = false;
            obj['speaker'] = {}
            obj['sec_int'] = parseInt(obj['sec']);
            obj['sec_float'] = parseFloat(obj['sec']);

            return obj;
        })
        return buffer;
    }

    const onSortClick = (index, direction) => {
        shouldStopVideo()
        // Determine which header to update based on the current view
        const headerToUpdate = getHeaderCurrentView();
        // Sort the currently displayed data (rows) instead of original data
        const dataToSort = [...rows];
        // Update the header arrows for the current view
        const newHeader = headerToUpdate.map((item, i) => {
            if (i === index) {
                item.isUp = !item.isUp
                return item
            }
            item.isUp = false
            return item
        })

        updateHeaderDataView(newHeader)

        // special case for order
        if (headerToUpdate[index].restore) {
            const field = 'order'
            const sorted = dataToSort.sort((a, b) => {
                if (a[field] > b[field]) return 1
                if (a[field] < b[field]) return -1
                return 0
            })
            // Directly set the sorted data since we're working with already filtered data
            setRows(sorted);
            return
        }

        let sortField = headerToUpdate[index].field.toLowerCase()
        sortField = getPrecisionField(sortField)
        dataToSort.sort((a, b) => {
            if (direction === 'up') {
                return a[sortField] > b[sortField] ? 1 : -1;
            } else {
                return a[sortField] < b[sortField] ? 1 : -1;
            }
        })
        // Directly set the sorted data since we're working with already filtered data
        setRows(dataToSort);

    }

    const indeterminateCheck = (newRows) => {
        const howManySelected = filterStore.howManySelectedSentences(currentView)
        const totalRowsForView = newRows.length

        if (howManySelected === 0) {
            setChecked(prevChecked => ({
                ...prevChecked,
                [currentView]: [false, false]
            }));
            userStore.setTotalDuration(durationFormat(0));
        } else if (howManySelected === totalRowsForView) {
            setChecked(prevChecked => ({
                ...prevChecked,
                [currentView]: [true, false]
            }));
        } else {
            setChecked(prevChecked => ({
                ...prevChecked,
                [currentView]: [false, true]
            }));
        }
    }

    const onCheckAll = (event) => {
        const isChecked = event.target.checked
        // Get only the currently visible/filtered rows
        checkUncheck(isChecked)
        calcDuration(rows)
    }

    const checkUncheck = (isChecked) => {
        if (isChecked) {
            rows.forEach((obj, index) => {
                obj.order = index + 1;
            });
            // Update the selection in the store
            filterStore.setAllSelectedSentences(rows, currentView);
            // Update the selected count
            setSelectedCount(prevCount => ({
                ...prevCount,
                [currentView]: rows.length
            }));
            // Set order to the number of rows + 1 for the next selection
            setOrder(prevOrder => ({
                ...prevOrder,
                [currentView]: rows.length + 1
            }));
            // Update checked state for current view
            setChecked(prevChecked => ({
                ...prevChecked,
                [currentView]: [true, false] // checked, not indeterminate
            }));

        } else {
            // Reset order to maxLines for all rows
            rows.forEach((obj, index) => {
                obj.order = maxLines
            });
            // Reset selection in the store
            filterStore.resetSelectedSentences(currentView);
            // Reset the selected count
            setSelectedCount(prevCount => ({
                ...prevCount,
                [currentView]: 0
            }));
            // Reset order for new selections
            setOrder(prevOrder => ({
                ...prevOrder,
                [currentView]: 1
            }));
            // Update checked state
            setChecked(prevChecked => ({
                ...prevChecked,
                [currentView]: [false, false] // not checked, not indeterminate
            }));
        }
    }

    const onCheckboxChange = (event, item, id) => {
        let newState = maxLines
        const dataForCurrentView = getDataForCurrentView();
        const currentOrder = order[currentView];

        console.log('duration', userStore.getTotalDuration(), userStore.getDuration())
        if (event.target.checked) {

            filterStore.setSelectedSentences(id, currentView)
            setOrder(prevOrder => ({
                ...prevOrder,
                [currentView]: prevOrder[currentView] + 1
            }));
            newState = currentOrder;

            // Update selected count
            const newSelectedCount = selectedCount[currentView] + 1;
            setSelectedCount(prevCount => ({
                ...prevCount,
                [currentView]: newSelectedCount
            }));

            // Update checkbox state
            const isAllSelected = newSelectedCount === dataForCurrentView.length;
            setChecked(prevChecked => ({
                ...prevChecked,
                [currentView]: isAllSelected ? [true, false] : [false, true]
            }));

        } else {
            // Unchecking - normal behavior
            filterStore.removeSelectedSentence(id, currentView)

            // Update selected count
            const newSelectedCount = selectedCount[currentView] - 1;
            setSelectedCount(prevCount => ({
                ...prevCount,
                [currentView]: newSelectedCount
            }));

            // Update checkbox state
            setChecked(prevChecked => ({
                ...prevChecked,
                [currentView]: newSelectedCount === 0 ? [false, false] : [false, true]
            }));
        }

        if (item < rows.length) {
            let newRows = rows.slice();
            newRows[item]['order'] = newState;
            setRows(newRows);
        }
    }

    const makeMidia = (selectedOptions) => {

        const createMidia = async (selectedOptions) => {

            // Get current search text
            const searchText = filterStore.getSearch(currentView);
            // Create a base data object
            const baseData = {
                'list_of_ids': filterStore.getSelectedSentences(currentView),
                'token': userStore.getToken(),
                'transcript_id': userStore.getUserTranscriptId(),
                'download_id': userStore.getUserTranscriptId(), // Use transcript ID as download ID
                'title': userStore.getUserTranscriptTitle(),
                'search_text': searchText,
                'model': mapViewToModel[currentView],
            };

            // Process each selected option
            const results = await Promise.all(selectedOptions.map(async (option) => {
                try {
                    // Create a data object for this specific kind
                    const data = {
                        ...baseData,
                        'kind': optionToKind[option]
                    };
                    // Prepare URL encoded data
                    const urlEncodedData = new URLSearchParams();
                    for (const [key, value] of Object.entries(data)) {
                        if (value !== null) {
                            urlEncodedData.append(key, value);
                        }
                    }

                    // Make API call
                    const response = await fetch(makeVideoUrl, {
                        method: 'POST',
                        mode: 'cors',
                        body: urlEncodedData,
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                            'X-CSRFToken': getCookies('csrftoken'),
                        }
                    });

                    const result = await response.json();

                    if (response.status !== 200) {
                        messageStore.setMessage(`Failed to create ${option}: ${result.message}`, 'error');
                        return { option, success: false, message: result.message };
                    }

                    return { option, success: true };
                } catch (error) {
                    messageStore.setMessage(`Error creating ${option}: ${error.message}`, 'error');
                    return { option, success: false, message: error.message };
                }
            }));
            // Process results
            const successfulOperations = results.filter(r => r.success);
            // Show a summary message
            if (successfulOperations.length > 0) {
                messageStore.setMessage('Files are ready to be processed, an email will be sent when they are ready', 'success')
            }
            return results;
        }
        createMidia(selectedOptions).then(() => {
        }).catch((error) => {
            console.error("Error in createMidia:", error);
        })
    }

    const onPrecisionChange = () => {
        setPrecision(prevPrecision => {
            const newPrecision = !prevPrecision[currentView];
            return {
                ...prevPrecision,
                [currentView]: newPrecision
            };
        });
    }

    const onVideoWidthChange = (width) => {
        filterStore.setVideoWidth(width, currentView);
    }

    const onJumpToItem = (itemData) => {
        // Close the search modal first
        if (closeSearchModalRef) {
            closeSearchModalRef();
        }

        // Clear the search to show all frames
        filterStore.setSearch(currentView, '');

        // Apply the filter clearing
        filterAll();

        // Give time for the filtering to complete and UI to update
        setTimeout(() => {
            // Now find the item in the full timeline (after search is cleared)
            const fullData = getDataForCurrentView();
            const itemIndex = fullData.findIndex(row => row.id === itemData.id);

            console.log('Jump to item debug:', {
                itemId: itemData.id,
                sentence: itemData.sentence?.substring(0, 30) + '...',
                foundAtIndex: itemIndex,
                totalItems: fullData.length
            });

            if (itemIndex !== -1 && virtuosoRef.current) {
                virtuosoRef.current.scrollToIndex({
                    index: itemIndex,
                    align: 'center',
                    behavior: 'smooth'
                });
            }
        }, 100);
    };

    const onViewChange = (view) => {
        setCurrentView(view)
        setViewConfig(CURRENT_VIEW_CONFIG[view])
        const newRows = [...getDataForCurrentView(view)]
        setRows(newRows)
        console.log('onViewChange', view, 'rows:', newRows.length)

        // Reset filters for the new view using the view parameter instead of currentView
        resetFilters(newRows, view);
        indeterminateCheck(newRows);
        // calcDuration(newRows);
        updateHeader();

        // No need to call filterAll() here since we already have the correct data
        // and filters are reset. The data will be filtered when user interacts with filters.
    }

    if (loading) return <Loading value={loadingProgress} />

    const renderCurrentView = (toolbarPosition) => {
        switch (currentView) {
            case 'Cherry Pick':
                return <CherryPickGrid
                    toolbarPosition={toolbarPosition}
                    data={rows}
                    filterStore={filterStore}
                    videoFile={videoFile}
                    onVideoClick={onVideoClick}
                    bottomOffset={(toolbarPosition === 'bottom' ? DRAG_TOOLBAR_HEIGHT : 0)}
                    currentView={currentView}
                />;
            case 'Charts':
                return <ChartsGrid toolbarPosition={toolbarPosition} data={rows} />;
            case 'AIB':
            case 'Entities':
            case 'Categories':
            case 'Auto Highlight':
            case 'Content Moderation':
            case 'Create a Video':
                calcDuration(rows)
                break
            default:
                console.log('default should not happen')
        }
        return <TableVirtuoso
            ref={virtuosoRef}
            scrollerRef={(ref) => { scrollerRef.current = ref; }}
            data={rows}
            style={{
                overflow: 'auto',
                height: '100%',
                width: '100%',
                boxSizing: 'border-box',
                ...getContentStyle(toolbarPosition)
            }}
            components={{
                // Override the default Table component to add responsive styling
                Table: React.forwardRef((props, ref) => (
                    <table
                        {...props}
                        ref={ref}
                        style={{
                            ...props.style,
                            tableLayout: 'auto',
                            width: '100%',
                            borderCollapse: 'collapse',
                            minWidth: 'fit-content'
                        }}
                        className="responsive-table"
                    />
                )),
                // Add responsive styling for TableRow
                TableRow: React.forwardRef((props, ref) => (
                    <tr
                        {...props}
                        ref={ref}
                        className={`${props.className || ''} responsive-table-row`}
                    />
                )),
                // Add TableHead component to ensure consistent styling with body
                TableHead: React.forwardRef((props, ref) => (
                    <thead
                        {...props}
                        ref={ref}
                        style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: theme.palette.background.paper,
                            borderBottom: `1px solid ${theme.palette.divider}`
                        }}
                    />
                ))
            }}
            fixedHeaderContent={() =>
                <Header
                    header={getHeaderCurrentView()}
                    onClick={onSortClick}
                    checked={checked[currentView]}
                    handleCheck={onCheckAll}
                    userStore={userStore}
                    precision={precision[currentView]}
                    filterStore={filterStore}
                    currentView={currentView}
                />}
            itemContent={(index, rowData) =>
                <ItemContent item={index} data={rowData} header={getHeaderCurrentView()}
                    filterStore={filterStore}
                    videoFile={videoFile}
                    onVideoClick={onVideoClick}
                    onCheckboxChange={onCheckboxChange}
                    currentView={currentView}
                    userStore={userStore}
                    precision={precision[currentView]}
                    onJumpToItem={onJumpToItem}
                />
            }
            rangeChanged={handleRangeChanged}
        />
    };
    return (

        <Box sx={{
            position: 'absolute',
            width: '100%',
            top: DRAG_TOOLBAR_HEIGHT,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            overflow: 'hidden',
        }}>
            <DraggableToolbar
                viewConfig={viewConfig}
                onPositionChange={handleToolbarPositionChange}
                rowCount={countRows()}
                selectedCount={selectedCount[currentView]}
                totalCount={originalRows.length}
                sentiments={filterStore.getSentiments(currentView)}
                onSentimentChange={onSentimentChange}
                speakers={filterStore.getSpeakers(currentView)}
                onSpeakerChange={onSpeakerChange}
                textLen={filterStore.getTextLen(currentView)}
                onLengthChange={onTextLenChange}
                seconds={filterStore.getSeconds(currentView)}
                onSecondsChange={onSecChange}
                onResetFilters={onResetFilters}
                subtitlesEnabled={filterStore.getSubtitlesOnOff(currentView)}
                onSetResetSubtitles={onSetResetSubtitles}
                spacesOnOff={filterStore.getSpacesOnOff(currentView)}
                onSetSpaces={onSetSpaces}
                onSearchChange={onSearchChange}
                searchValue={filterStore.getSearch(currentView)}
                store={filterStore}
                currentView={currentView}
                onViewChange={onViewChange}
                makeMidia={makeMidia}
                precision={precision[currentView]}
                onPrecisionChange={onPrecisionChange}
                videoWidth={filterStore.getVideoWidth(currentView)}
                onVideoWidthChange={onVideoWidthChange}
                userStore={userStore}
                onJumpToItem={onJumpToItem}
                setCloseSearchModalRef={setCloseSearchModalRef}
                videoFileExtension={videoExt}
            />

            {renderCurrentView(toolbarPosition)}

            {/* Scroll Time Display */}
            {scrollTimeDisplay.show && (
                <Box
                    sx={{
                        position: 'fixed',
                        left: scrollTimeDisplay.position.x,
                        top: scrollTimeDisplay.position.y,
                        zIndex: 1500,
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(40, 40, 40, 0.95)'
                            : 'rgba(255, 255, 255, 0.95)',
                        color: theme.palette.text.primary,
                        padding: '8px 16px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: `1px solid ${theme.palette.divider}`,
                        backdropFilter: 'blur(8px)',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        pointerEvents: 'none',
                        transition: 'opacity 0.2s ease-in-out',
                        opacity: scrollTimeDisplay.show ? 1 : 0,
                    }}
                >
                    {scrollTimeDisplay.time}
                </Box>
            )}

        </Box>

    )
}

export default observer(CreateVideo);